import React, { useRef, useEffect, useState } from 'react';
import { Box, TextField } from '@mui/material';

interface Props {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  onComplete?: (value: string) => void;
}

const OtpInput: React.FC<Props> = ({
  length = 6,
  value,
  onChange,
  disabled = false,
  error = false,
  onComplete,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  useEffect(() => {
    // Focus first empty input
    const firstEmptyIndex = value.length < length ? value.length : length - 1;
    if (inputRefs.current[firstEmptyIndex] && !disabled) {
      inputRefs.current[firstEmptyIndex]?.focus();
    }
  }, [value, length, disabled]);

  const handleChange = (index: number, newValue: string) => {
    // Only allow digits
    const digit = newValue.replace(/\D/g, '').slice(0, 1);
    
    if (digit) {
      const newCode = value.split('');
      newCode[index] = digit;
      const updatedCode = newCode.join('').slice(0, length);
      onChange(updatedCode);
      
      // Move to next input if available
      if (index < length - 1 && updatedCode.length === index + 1) {
        inputRefs.current[index + 1]?.focus();
      }
      
      // Call onComplete if all digits are filled
      if (updatedCode.length === length && onComplete) {
        onComplete(updatedCode);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (value[index]) {
        // Clear current digit
        const newCode = value.split('');
        newCode[index] = '';
        onChange(newCode.join(''));
      } else if (index > 0) {
        // Move to previous input and clear it
        const newCode = value.split('');
        newCode[index - 1] = '';
        onChange(newCode.join(''));
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pastedData) {
      onChange(pastedData);
      // Focus the next empty input or the last one
      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0,
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
      onPaste={handlePaste}
    >
      {Array.from({ length }).map((_, index) => (
        <TextField
          key={index}
          inputRef={(el) => {
            inputRefs.current[index] = el;
          }}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)}
          onFocus={() => setFocusedIndex(index)}
          onBlur={() => setFocusedIndex(null)}
          disabled={disabled}
          error={error}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: 'xx-large',
              fontWeight: 400,
              padding: 0,
              width: '35px',
              height: '50px',
            },
          }}
          sx={{
            width: '35px',
            '& .MuiOutlinedInput-root': {
              height: '50px',
              '& fieldset': {
                borderColor: error
                  ? '#d32f2f'
                  : focusedIndex === index
                  ? '#1083c6'
                  : '#C4C4C4',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: error ? '#d32f2f' : '#C4C4C4',
              },
              '&.Mui-focused fieldset': {
                borderColor: error ? '#d32f2f' : '#1083c6',
                borderWidth: '1.5px',
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

export default OtpInput;

